import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';
import PrelitXLogo from '@/components/PrelitXLogo';

const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await login(email, password);
      toast.success('Welcome back!');
      // ALWAYS redirect to dashboard after successful login
      navigate('/dashboard', { replace: true });
    } catch (error) {
      console.error('Login error:', error);
      toast.error(error.response?.data?.detail || 'Invalid email or password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="page-container min-h-screen flex items-center justify-center px-4">
      <Card 
        className="w-full max-w-md shadow-2xl border-0 glass-card"
      >
        <CardHeader className="text-center space-y-4 pb-8">
          <div className="mx-auto mb-2" style={{ marginTop: '24px' }}>
            <PrelitXLogo size="large" className="justify-center" />
          </div>
          <div>
            <CardTitle className="text-2xl font-bold" style={{ color: '#245451' }}>
              Welcome Back
            </CardTitle>
            <CardDescription className="mt-2" style={{ color: '#64748B' }}>
              Sign in to access your medical chronology workspace
            </CardDescription>
          </div>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="email" style={{ color: '#1A1A1A', fontWeight: 500 }}>Email Address</Label>
              <Input
                id="email"
                type="email"
                placeholder="attorney@lawfirm.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="h-11"
                style={{ borderColor: '#E2E8F0', backgroundColor: '#FFFFFF' }}
                data-testid="login-email-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" style={{ color: '#1A1A1A', fontWeight: 500 }}>Password</Label>
              <Input
                id="password"
                type="password"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="h-11"
                style={{ borderColor: '#E2E8F0', backgroundColor: '#FFFFFF' }}
                data-testid="login-password-input"
              />
            </div>
            <Button
              type="submit"
              className="w-full h-11 font-medium"
              style={{ backgroundColor: '#245451' }}
              disabled={loading}
              data-testid="login-submit-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Signing in...
                </>
              ) : (
                'Sign In'
              )}
            </Button>
          </form>

          <div className="mt-6 space-y-3">
            <div className="text-center text-sm">
              <Link 
                to="/forgot-password" 
                className="font-medium"
                style={{ color: '#406962' }}
              >
                Forgot your password?
              </Link>
            </div>
            <div className="text-center text-sm" style={{ color: '#64748B' }}>
              Don&apos;t have an account?{' '}
              <Link 
                to="/register" 
                className="font-medium"
                style={{ color: '#406962' }}
              >
                Create one
              </Link>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default LoginPage;
